import { useState } from "react";
import { Alert, FileUploadField, Text } from "@marekl/adminer/components";
import { asApiErrorBody } from "@marekl/adminer/api";
import { ApiError } from "../../lib/api";
import { MusicPlayer } from "../MusicPlayer";
import { MusicFileSummary } from "../MusicFileSummary";
import type { MusicAudience } from "../../lib/musicState";
import { musicWarningItems } from "../../lib/musicState";
import type { EntryMusic } from "../../lib/queries/music";
import styles from "./MusicPanel.module.css";

// Keep in sync with the server's music.max_upload_bytes (30 MB by
// default). The guard is a courtesy — it skips a round trip the backend
// would refuse with 413 anyway — so a server configured LOWER still wins,
// and its 413 carries the real number. That is why the message below
// does not name a size: promising 30 MB where the server allows 10 is
// worse than not promising one.
const MAX_MUSIC_BYTES = 30 * 1024 * 1024;

// MusicPanel is THE surface for a přihláška's recording: the empty drop
// zone before there is one, the waveform and its verdicts after. The
// entry list opens it in a modal, the wizard renders it inline as its
// last step, and they are the same component so the two cannot drift
// into two different ideas of what uploading music looks like.
//
// The recording can be REPLACED but not removed, exactly like a member's
// photo: a competitor on a start list is supposed to have music, so "no
// music" is a gap to close rather than a state to return to.
//
// Warnings never block. Overtime is the technical judge's business and
// the system must not be stricter than the rules — the panel says what
// it measured and lets the leader decide.
export function MusicPanel({
  src,
  music,
  disabled,
  disabledReason,
  audience = "team",
  upload,
  onUploaded,
}: {
  /** Authenticated URL the recording plays from. A URL rather than an
   * entry id because the same panel serves a discipline's official ČNSMS
   * track, which hangs off no entry at all. */
  src: string;
  music: EntryMusic | null;
  disabled?: boolean;
  disabledReason?: string;
  /** Decides whether a warning may mention the grace band; see
   * warningMessage. Defaults to the club's view. */
  audience?: MusicAudience;
  upload: {
    mutateAsync: (file: File) => Promise<EntryMusic>;
    isPending: boolean;
    error: ApiError | null;
  };
  onUploaded?: (music: EntryMusic) => void;
}) {
  const [current, setCurrent] = useState<EntryMusic | null>(music);
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleFile(file: File) {
    if (disabled) return;
    // The size guard stays ours rather than going through `maxSize`: the
    // advice („uložit v nižší kvalitě") is what a leader can act on, and a
    // generic "file too large" is not. `FileUploadField` owns the input
    // reset either way, so a re-pick of the same file still works.
    if (file.size > MAX_MUSIC_BYTES) {
      setLocalError("Soubor je příliš velký. Zkuste nahrávku uložit v nižší kvalitě.");
      return;
    }
    setLocalError(null);
    try {
      const result = await upload.mutateAsync(file);
      setCurrent(result);
      onUploaded?.(result);
    } catch {
      // The message surfaces through upload.error.
    }
  }

  const error = localError ?? errMessage(upload.error);

  return (
    <div className={styles.panel}>
      {/* One primitive, two shapes, switched by state — which is what the
          variants are for (0.31.1).

          `zone` while there is nothing: a full-width dashed drop area, which
          is what an empty music slot should look like. `inline` once there
          is a recording: the player and the summary at their natural size
          with a compact „Vyměnit hudbu" under them.

          Do NOT collapse this to one `zone` with the player in `preview`.
          That was tried: the dashed area never leaves, only the text inside
          it changes, and the screen looks identical before and after an
          upload. */}
      <FileUploadField
        accept="audio/mpeg,.mp3"
        onSelect={(file) => void handleFile(file)}
        disabled={disabled}
        busy={upload.isPending}
        // Ours goes through the primitive's slot rather than into an Alert of
        // our own: `FileUploadField` renders its OWN refusals there (a file
        // that is not MP3 never reaches `onSelect`), and a second alert
        // outside it would leave two messages about two different files on
        // screen at once, neither clearing the other. A string passes through
        // `apiErrorMessage` untouched, so the copy is unchanged.
        error={error}
        variant={current ? "inline" : "zone"}
        prompt="Přetáhněte sem MP3, nebo klikněte"
        hint={current ? undefined : "Jeden soubor ve formátu MP3."}
        triggerLabel="Vyměnit hudbu"
        triggerVariant="primary"
        preview={
          current ? (
            <>
              <MusicPlayer
                // Replacing a recording does not change its URL, so without
                // a key the element would keep the bytes it already has.
                key={String(current.uploaded_at)}
                src={src}
              />
              <MusicFileSummary
                name={current.original_name}
                durationMs={current.duration_ms}
                bitrateKbps={current.bitrate_kbps}
                sizeBytes={current.size_bytes}
              />
            </>
          ) : undefined
        }
      />

      {/* Collapsed by label: a file with both a low bitrate and a low
          sample rate is one problem to the person fixing it, and two
          identical headings stacked on top of each other read as a
          rendering fault. */}
      {current &&
        musicWarningItems(current.warnings, audience).map((w) => (
          <Alert key={w.kind} variant={w.variant} title={w.label}>
            {w.message}
          </Alert>
        ))}

      {disabled && disabledReason && <Text variant="muted">{disabledReason}</Text>}
    </div>
  );
}

function errMessage(e: ApiError | null): string | undefined {
  if (!e) return undefined;
  // asApiErrorBody is the lib's single source of truth for the error
  // shape — it also recognises the legacy `error` field a bare cast
  // would miss.
  return asApiErrorBody(e.body)?.message ?? "Nahrání se nezdařilo.";
}
